import React from 'react';
import { Box, Typography } from '@mui/material';

import { PropertyTextInput } from '@paragon/client-tools';
import { ChordBracingSettings } from '@paragon/contract-library';
import { DistanceField } from '@paragon/material';

import { MultiValueBooleanInput } from '../settings/multi-value-controls/MultiValueBooleanInput';
import { MultiValueDistanceInput } from '../settings/multi-value-controls/MultiValueDistanceInput';

interface Props extends ChordBracingSettingsOwnProps {
  readonly title: string;
}

const doNothing = () => {};

class ChordBracingSettingsComponent extends React.Component<Props> {
  render() {
    const { calculatedMaxPurlinSpacing, chordBracingSettings, disabled, title } = this.props;

    let { onSetChordPurlinSpacing } = this.props;
    if (!onSetChordPurlinSpacing) {
      onSetChordPurlinSpacing = doNothing;
    }

    const allSettingsAreSheathed = chordBracingSettings.every((settings) => settings.isSheathed);
    const allSettingsUseMaxPurlinSpacing = chordBracingSettings.every((settings) => settings.useMaxPurlinSpacing);

    return (
      <Box
        sx={{
          flex: 1,
          m: 1,
          '&:not(:last-child)': {
            mb: 2,
          },
        }}
      >
        <Typography sx={{ pb: 1 }} variant="caption">{`${title} Bracing Settings`}</Typography>
        <Box
          sx={{
            border: 'solid 1px',
            borderColor: 'border.main',
            display: 'flex',
            flexDirection: 'column',

            p: 1,
            px: 2,
          }}
        >
          <MultiValueBooleanInput
            values={chordBracingSettings.map((settings) => settings.isSheathed)}
            onChange={(newIsSheathedValue) =>
              this.props.onSetIsSheathed ? this.props.onSetIsSheathed(newIsSheathedValue) : null
            }
            disabled={disabled}
            label="Is Sheathed"
          />

          <MultiValueBooleanInput
            values={chordBracingSettings.map((settings) => settings.useMaxPurlinSpacing)}
            onChange={(newUseMaxPurlinSpacingValue) =>
              this.props.onSetUseMaxPurlinSpacing
                ? this.props.onSetUseMaxPurlinSpacing(newUseMaxPurlinSpacingValue)
                : null
            }
            disabled={disabled || allSettingsAreSheathed}
            label="Use Max Purlin Spacing"
          />

          {allSettingsUseMaxPurlinSpacing ? (
            <PropertyTextInput disabled property="Purlin Spacing" value="Maximum Spacing" />
          ) : (
            <MultiValueDistanceInput
              label="Purlin Spacing"
              submit={onSetChordPurlinSpacing}
              values={chordBracingSettings.map((settings) => settings.purlinSpacing)}
              min={0.125}
              disabled={disabled || allSettingsAreSheathed || allSettingsUseMaxPurlinSpacing}
            />
          )}

          {calculatedMaxPurlinSpacing != null && chordBracingSettings.length === 1 && (
            <DistanceField label="Calculated Max Purlin Spacing" value={calculatedMaxPurlinSpacing} disabled />
          )}
        </Box>
      </Box>
    );
  }
}

type ChordBracingSettingsOwnProps = {
  readonly disabled?: boolean;
  readonly calculatedMaxPurlinSpacing: number | null;
  readonly chordBracingSettings: readonly ChordBracingSettings[];
  readonly onSetIsSheathed?: (isSheathed: boolean) => void;
  readonly onSetUseMaxPurlinSpacing?: (useMaxPurlinSpacing: boolean) => void;
  readonly onSetChordPurlinSpacing?: (value: number) => void;
};
type ChordBracingSettingsProps = ChordBracingSettingsOwnProps;

export const SlopedTopChordBracingSettings: React.FunctionComponent<ChordBracingSettingsProps> = (props) => (
  <ChordBracingSettingsComponent title="Sloped Top Chord" {...props} />
);

export const FlatTopChordBracingSettings: React.FunctionComponent<ChordBracingSettingsProps> = (props) => (
  <ChordBracingSettingsComponent title="Flat Top Chord" {...props} />
);

export const BottomChordBracingSettings: React.FunctionComponent<ChordBracingSettingsProps> = (props) => (
  <ChordBracingSettingsComponent title="Bottom Chord" {...props} />
);
